import { useEffect, useRef, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { isAutomatedTests } from '../utils/isAutomatedTests';

// Kept in sync with the keyframe duration below so the "entering" class is removed once the one-shot
// animation has finished.
const ENTER_MS = 450;

const useStyles = createUseStyles({
    '@keyframes insightEnter': {
        from: { opacity: 0, transform: 'translateY(-6px)' },
        to: { opacity: 1, transform: 'none' },
    },
    // Additive: animates opacity/transform only, so the row holds its space and its siblings don't
    // reflow as it enters.
    entering: {
        animation: `$insightEnter ${ENTER_MS}ms ease-out`,
    },
});

// Plays a one-shot enter-animation on insight rows that have newly arrived since the previous render
// (e.g. a realtime reconcile pushing in a fresh insight) -- but never on the initial list, so opening
// a surface doesn't animate everything at once. Mirrors the prev-ref diff + self-clearing highlight
// TasksList uses. Returns a per-id className resolver; callers spread it onto the row wrapper.
// Disabled under automated tests to avoid e2e timing flake (matching AnimatedCollapse).
export const useInsightEnterAnimation = (orderedIds: string[]): ((insightId: string) => string | undefined) => {
    const classes = useStyles();
    const [enteringIds, setEnteringIds] = useState<Set<string>>(new Set());
    const seenRef = useRef<Set<string> | null>(null);

    // Diff current vs. previous ids by a stable key so the effect runs only when the set changes,
    // not on every render (getActive*/slice produce a fresh array each time).
    const idsKey = orderedIds.join(',');
    useEffect(() => {
        const previouslySeen = seenRef.current;
        seenRef.current = new Set(orderedIds);

        // First render seeds the baseline (the initial list never animates); tests skip entirely.
        if (!previouslySeen || isAutomatedTests()) {
            return;
        }

        const fresh = orderedIds.filter(id => !previouslySeen.has(id));
        if (fresh.length === 0) {
            return;
        }

        setEnteringIds(prev => new Set([...prev, ...fresh]));
        const timer = setTimeout(() => {
            setEnteringIds(prev => {
                const next = new Set(prev);
                for (const id of fresh) {
                    next.delete(id);
                }
                return next;
            });
        }, ENTER_MS + 100);
        return () => clearTimeout(timer);
        // idsKey stands in for orderedIds in the diff; depending on the array itself would re-run every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idsKey]);

    return (insightId: string): string | undefined => (enteringIds.has(insightId) ? classes.entering : undefined);
};
